import React, { useState } from 'react';
import { XIcon, PlusIcon, ChevronDownIcon, ChevronUpIcon } from 'lucide-react';
import { Button } from '../ui/Button';
interface CreateAlertModalProps {
  onClose: () => void;
  onSave: (data: any) => void;
}
export function CreateAlertModal({
  onClose,
  onSave
}: CreateAlertModalProps) {
  const [alertName, setAlertName] = useState('');
  const [selectedCountries, setSelectedCountries] = useState<string[]>([]);
  const [selectedSectors, setSelectedSectors] = useState<{
    [key: string]: string[];
  }>({});
  const [frequency, setFrequency] = useState('instant');
  const [keywords, setKeywords] = useState('');
  const [expandedSectors, setExpandedSectors] = useState<string[]>([]);
  // Mock data for sectors and subsectors
  const sectorsData = [{
    id: 'tech',
    name: 'Technologies & Télécoms',
    subsectors: [{
      id: 'tech-materiel',
      name: 'Matériels et Équipements Informatique'
    }, {
      id: 'tech-logiciels',
      name: 'Logiciels et Solutions'
    }, {
      id: 'tech-services',
      name: 'Services Numériques'
    }, {
      id: 'tech-telecom',
      name: 'Infrastructures Télécoms'
    }]
  }, {
    id: 'construction',
    name: 'BTP & Construction',
    subsectors: [{
      id: 'construction-batiment',
      name: 'Bâtiment'
    }, {
      id: 'construction-genie',
      name: 'Génie civil'
    }, {
      id: 'construction-infra',
      name: 'Infrastructures routières'
    }, {
      id: 'construction-materiaux',
      name: 'Matériaux de construction'
    }]
  }, {
    id: 'energy',
    name: 'Énergie & Utilities',
    subsectors: [{
      id: 'energy-renouvelable',
      name: 'Énergies renouvelables'
    }, {
      id: 'energy-electricite',
      name: 'Équipements Électriques'
    }, {
      id: 'energy-eau',
      name: "Traitement de l'eau"
    }, {
      id: 'energy-gaz',
      name: 'Gaz et pétrole'
    }]
  }, {
    id: 'services',
    name: 'Services & Conseil',
    subsectors: [{
      id: 'services-strategie',
      name: 'Conseil en Stratégie'
    }, {
      id: 'services-finance',
      name: 'Services Financiers'
    }, {
      id: 'services-juridique',
      name: 'Conseil Juridique'
    }, {
      id: 'services-rh',
      name: 'Ressources Humaines'
    }]
  }, {
    id: 'health',
    name: 'Santé & Pharmaceutique',
    subsectors: [{
      id: 'health-equipement',
      name: 'Équipements médicaux'
    }, {
      id: 'health-pharma',
      name: 'Produits pharmaceutiques'
    }, {
      id: 'health-services',
      name: 'Services de santé'
    }]
  }, {
    id: 'vehicles',
    name: 'Véhicules et Accessoires',
    subsectors: [{
      id: 'vehicles-auto',
      name: 'Automobiles'
    }, {
      id: 'vehicles-pieces',
      name: 'Pièces détachées'
    }, {
      id: 'vehicles-transport',
      name: 'Transport collectif'
    }, {
      id: 'vehicles-special',
      name: 'Véhicules spéciaux'
    }]
  }, {
    id: 'marketing',
    name: 'Marketing Digital',
    subsectors: [{
      id: 'marketing-influence',
      name: "Campagnes de Marketing d'Influence"
    }, {
      id: 'marketing-email',
      name: 'Automatisation du Marketing par Email'
    }, {
      id: 'marketing-contenu',
      name: 'Stratégies de Marketing de Contenu'
    }, {
      id: 'marketing-social',
      name: 'Marketing et Gestion des Médias Sociaux'
    }, {
      id: 'marketing-ppc',
      name: 'Gestion de la Publicité au Paiement Par Clic (PPC)'
    }, {
      id: 'marketing-seo',
      name: "Services d'Optimisation pour les Moteurs de Recherche (SEO)"
    }]
  }];
  // Countries data
  const countriesData = ['Tunisie', 'Maroc', 'Algérie', 'Sénégal', "Côte d'Ivoire", 'Cameroun', 'Nigeria', 'Kenya', 'Égypte'];
  const toggleSectorExpansion = (sectorId: string) => {
    if (expandedSectors.includes(sectorId)) {
      setExpandedSectors(expandedSectors.filter(id => id !== sectorId));
    } else {
      setExpandedSectors([...expandedSectors, sectorId]);
    }
  };
  const toggleSector = (sectorId: string) => {
    const updatedSectors = {
      ...selectedSectors
    };
    if (sectorId in updatedSectors) {
      // If sector is already selected, remove it and all its subsectors
      delete updatedSectors[sectorId];
    } else {
      // Otherwise add it with empty subsectors array
      updatedSectors[sectorId] = [];
    }
    setSelectedSectors(updatedSectors);
  };
  const toggleSubsector = (sectorId: string, subsectorId: string) => {
    const updatedSectors = {
      ...selectedSectors
    };
    // Initialize sector if not already selected
    if (!(sectorId in updatedSectors)) {
      updatedSectors[sectorId] = [];
    }
    const subsectors = updatedSectors[sectorId];
    if (subsectors.includes(subsectorId)) {
      // Remove subsector if already selected
      updatedSectors[sectorId] = subsectors.filter(id => id !== subsectorId);
    } else {
      // Add subsector
      updatedSectors[sectorId] = [...subsectors, subsectorId];
    }
    setSelectedSectors(updatedSectors);
  };
  const toggleCountry = (country: string) => {
    if (selectedCountries.includes(country)) {
      setSelectedCountries(selectedCountries.filter(c => c !== country));
    } else {
      setSelectedCountries([...selectedCountries, country]);
    }
  };
  const handleSubmit = () => {
    const keywordArray = keywords.split(',').map(k => k.trim()).filter(k => k);
    const alertData = {
      name: alertName,
      sectors: selectedSectors,
      countries: selectedCountries,
      keywords: keywordArray,
      frequency
    };
    onSave(alertData);
  };
  // Count total selected sectors and subsectors
  const countSelectedItems = () => {
    const sectorCount = Object.keys(selectedSectors).length;
    const subsectorCount = Object.values(selectedSectors).reduce((acc, subsectors) => acc + subsectors.length, 0);
    return {
      sectorCount,
      subsectorCount
    };
  };
  const {
    sectorCount,
    subsectorCount
  } = countSelectedItems();
  return <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" data-id="element-67">
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden" data-id="element-68">
        <div className="flex items-center justify-between p-6 border-b border-gray-200" data-id="element-69">
          <h2 className="text-xl font-bold text-gray-900" data-id="element-70">
            Créer une nouvelle alerte
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-500" data-id="element-71">
            <XIcon size={20} data-id="element-72" />
          </button>
        </div>
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]" data-id="element-73">
          <div className="space-y-6" data-id="element-74">
            {/* Alert name */}
            <div data-id="element-75">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-76">
                Nom de l'alerte <span className="text-red-500" data-id="element-77">*</span>
              </label>
              <input type="text" value={alertName} onChange={e => setAlertName(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500" placeholder="Ex: Projets BTP au Maroc" data-id="element-78" />
            </div>
            {/* Keywords */}
            <div data-id="element-79">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-80">
                Mots-clés
              </label>
              <textarea value={keywords} onChange={e => setKeywords(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500" placeholder="Ex: construction, infrastructure, route (séparés par des virgules)" rows={3} data-id="element-81"></textarea>
              <p className="mt-1 text-xs text-gray-500" data-id="element-82">
                Séparez les mots-clés par des virgules. Ces termes seront
                recherchés dans les titres et descriptions des appels d'offres.
              </p>
            </div>
            {/* Sectors and subsectors */}
            <div data-id="element-83">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-84">
                Secteurs et sous-secteurs d'activité{' '}
                <span className="text-red-500" data-id="element-85">*</span>
              </label>
              <div className="border border-gray-300 rounded-md overflow-hidden" data-id="element-86">
                <div className="p-3 bg-gray-50 border-b border-gray-300 flex justify-between items-center" data-id="element-87">
                  <span className="text-sm font-medium text-gray-700" data-id="element-88">
                    {sectorCount > 0 ? <>
                        {sectorCount} secteur{sectorCount > 1 ? 's' : ''} et{' '}
                        {subsectorCount} sous-secteur
                        {subsectorCount > 1 ? 's' : ''} sélectionné
                        {subsectorCount > 1 ? 's' : ''}
                      </> : 'Sélectionnez au moins un secteur'}
                  </span>
                  {Object.keys(selectedSectors).length > 0 && <button type="button" className="text-xs text-blue-600 hover:text-blue-800" onClick={() => setSelectedSectors({})} data-id="element-89">
                      Tout effacer
                    </button>}
                </div>
                <div className="max-h-60 overflow-y-auto" data-id="element-90">
                  {sectorsData.map(sector => <div key={sector.id} className="border-b border-gray-200 last:border-b-0" data-id="element-91">
                      <div className="flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-gray-50" onClick={() => toggleSectorExpansion(sector.id)} data-id="element-92">
                        <div className="flex items-center" data-id="element-93">
                          <div className="mr-3" data-id="element-94">
                            <input type="checkbox" checked={sector.id in selectedSectors} onChange={e => {
                          e.stopPropagation();
                          toggleSector(sector.id);
                        }} className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" data-id="element-95" />
                          </div>
                          <span className="font-medium" data-id="element-96">{sector.name}</span>
                          {sector.id in selectedSectors && selectedSectors[sector.id].length > 0 && <span className="ml-2 text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full" data-id="element-97">
                                {selectedSectors[sector.id].length} sous-secteur
                                {selectedSectors[sector.id].length > 1 ? 's' : ''}
                              </span>}
                        </div>
                        {expandedSectors.includes(sector.id) ? <ChevronUpIcon size={16} className="text-gray-400" data-id="element-98" /> : <ChevronDownIcon size={16} className="text-gray-400" data-id="element-99" />}
                      </div>
                      {expandedSectors.includes(sector.id) && <div className="bg-gray-50 pl-12 pr-4 py-2 border-t border-gray-200" data-id="element-100">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-2" data-id="element-101">
                            {sector.subsectors.map(subsector => <div key={subsector.id} className="flex items-center" data-id="element-102">
                                <input type="checkbox" id={subsector.id} checked={sector.id in selectedSectors && selectedSectors[sector.id].includes(subsector.id)} onChange={() => toggleSubsector(sector.id, subsector.id)} className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" data-id="element-103" />
                                <label htmlFor={subsector.id} className="ml-2 text-sm text-gray-700" data-id="element-104">
                                  {subsector.name}
                                </label>
                              </div>)}
                          </div>
                        </div>}
                    </div>)}
                </div>
              </div>
            </div>
            {/* Countries */}
            <div data-id="element-105">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-106">
                Pays <span className="text-red-500" data-id="element-107">*</span>
              </label>
              <div className="border border-gray-300 rounded-md p-3" data-id="element-108">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2" data-id="element-109">
                  {countriesData.map(country => <div key={country} className="flex items-center" data-id="element-110">
                      <input type="checkbox" id={`country-${country}`} checked={selectedCountries.includes(country)} onChange={() => toggleCountry(country)} className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" data-id="element-111" />
                      <label htmlFor={`country-${country}`} className="ml-2 text-sm text-gray-700" data-id="element-112">
                        {country}
                      </label>
                    </div>)}
                </div>
              </div>
            </div>
            {/* Notification frequency */}
            <div data-id="element-113">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-114">
                Fréquence des notifications
              </label>
              <select value={frequency} onChange={e => setFrequency(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500" data-id="element-115">
                <option value="instant" data-id="element-116">Instantanée</option>
                <option value="daily" data-id="element-117">Résumé quotidien</option>
                <option value="weekly" data-id="element-118">Résumé hebdomadaire</option>
              </select>
            </div>
          </div>
        </div>
        <div className="p-6 bg-gray-50 border-t border-gray-200 flex justify-end space-x-3" data-id="element-119">
          <Button variant="outline" onClick={onClose} className="border-gray-300 text-gray-700" data-id="element-120">
            Annuler
          </Button>
          <Button variant="primary" onClick={handleSubmit} disabled={!alertName || Object.keys(selectedSectors).length === 0 || selectedCountries.length === 0} className="shadow-md shadow-yellow-500/20" data-id="element-121">
            <PlusIcon size={16} className="mr-2" data-id="element-122" />
            Créer l'alerte
          </Button>
        </div>
      </div>
    </div>;
}